import { useState } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { User, LogOut, Settings, ChevronDown, ChevronRight, Search } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { ConnectionStatus } from '@/components/ui/ConnectionStatus'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
// Imported from its source file, not the '@/features/search' barrel: the
// barrel also re-exports SearchPage, which App.tsx lazy-loads — a static
// import of the barrel here (Header renders on every authenticated page)
// forced SearchPage back into the main chunk (INEFFECTIVE_DYNAMIC_IMPORT).
import { CompactSearchBar } from '@/features/search/components/SearchBar'
import { NotificationBell } from '@/features/notifications'
import { SupportButton } from '@/features/support/components/SupportButton'
import { usePersona } from '@/hooks/usePersona'
import { getBreadcrumbs } from './navigation'

export function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, hasRole } = useAuth()
  const persona = usePersona()
  const [showUserMenu, setShowUserMenu] = useState(false)

  const breadcrumbs = getBreadcrumbs(location.pathname, hasRole('platform_admin'), user?.tenantName)

  // User badge shows three things: the person's name, their role, and the
  // active context (tenant name, or "Platform" for platform-scoped roles).
  // The NAME leads — it is the identity the badge is for. The context used to
  // lead, which read as the tenant being the identity.
  const displayName = user?.fullName || user?.name || 'User'
  const activeContext = persona.isPlatform ? 'Platform' : (user?.tenantName || 'Tenant')
  // Guard against a role that isn't in ROLE_LABELS (roleLabel would be undefined):
  // fall back to the raw role string, then a generic label, so we never render
  // "undefined".
  const roleLabel = persona.roleLabel || user?.role || 'Member'

  const handleLogout = async () => {
    // Local sign-out is synchronous inside logout(); the server revoke is
    // awaited (bounded) BEFORE navigating, because the navigation aborts any
    // in-flight request and an aborted sign-out used to leave an SSO session
    // alive. A failed revoke is surfaced on the login page, never hidden.
    const { serverRevoked } = await logout()
    // Hard navigation (#301): the store's synchronous cleanup has run; a full
    // page load also drops every in-memory module, stream and component
    // state of the previous user, which an SPA navigate() would keep.
    window.location.assign(serverRevoked ? '/login' : '/login?reason=signout_incomplete')
  }

  return (
    // relative z-30: `.beskar-glass` applies backdrop-filter, which creates a NEW
    // STACKING CONTEXT on this element. Without an explicit z-index here, the
    // header's IN-SUBTREE positioned descendants are confined to that context
    // and painted UNDER <main>, which comes later in DOM order — the "dropdown
    // renders behind page content" bug. NOTE (Aug-14): the user menu and
    // NotificationPanel now render via Radix Popover PORTALS to document.body,
    // so they are no longer governed by this context at all — the z-index
    // remains load-bearing for the header itself and any future in-subtree
    // positioned children. Fixed overlays inside <main> (modals at z-50)
    // still sit above this.
    <header className="relative z-30 flex h-16 items-center justify-between gap-2 border-b px-4 sm:px-6 md:rounded-tr-2xl">
      {/* Glass lives on an inset LAYER, not on <header> itself. backdrop-filter
          makes its element the containing block for `position: fixed`
          descendants — with it on the header, the user-menu's `fixed inset-0`
          click-away backdrop resolved against this 64px strip instead of the
          viewport, so clicking in the page no longer dismissed the menu.
          Painting the blur in a child restores viewport-anchored `fixed`
          while keeping the identical frosted look. */}
      <div className="beskar-glass pointer-events-none absolute inset-0 -z-10 md:rounded-tr-2xl" aria-hidden="true" />
      {/* min-w-0 flex-1: now that Layout's content column can shrink (min-w-0),
          let the breadcrumb side absorb the squeeze and clip/truncate WITHIN the
          header instead of pushing the header — and the whole page — wider than
          the viewport. overflow-hidden keeps a long breadcrumb chain contained. */}
      <div className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
        <nav className="flex min-w-0 items-center gap-1 overflow-hidden" aria-label="Breadcrumb">
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1
            // Responsive collapse: the CURRENT page title always shows (and is
            // the only part that truncates, with an ellipsis). Middle crumbs
            // ("Workspace") drop first (< md), then the root tenant crumb
            // (< sm) — at 390px the header reads just "Home".
            const visibility = isLast ? 'min-w-0' : i === 0 ? 'hidden shrink-0 sm:flex' : 'hidden shrink-0 md:flex'
            const chevronVisibility = isLast ? 'hidden sm:block' : 'block'
            return (
              <span key={i} className={`flex items-center gap-1 ${visibility}`}>
                {i > 0 && <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-gray-300 ${chevronVisibility}`} />}
                {isLast ? (
                  <h2 className="min-w-0 truncate text-lg font-semibold text-gray-900" title={crumb.label}>{crumb.label}</h2>
                ) : crumb.path ? (
                  <Link to={crumb.path} className="whitespace-nowrap text-sm font-medium text-gray-500 hover:text-gray-700">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="whitespace-nowrap text-sm font-medium text-gray-400">{crumb.label}</span>
                )}
              </span>
            )
          })}
        </nav>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1 sm:gap-4">
        {/* Search bar */}
        <CompactSearchBar className="hidden lg:block" />
        {/* Below lg the inline bar is hidden — keep search reachable with an
            icon button that opens the full search page. */}
        <Link
          to="/tenant/search"
          aria-label="Search"
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 lg:hidden"
        >
          <Search className="h-5 w-5" />
        </Link>

        {/* Connection status */}
        <ConnectionStatus />

        {/* Support */}
        <SupportButton />

        {/* Notifications */}
        <NotificationBell />

        {/* User menu — Radix Popover (same primitive as SupportButton and the
            notification bell): the previous conditional render carried
            enter-only animate-in classes, so the menu faded in but vanished in
            one frame on close (unmount-before-exit). Radix keeps the node
            mounted through the data-[state=closed] exit animation, and its
            dismissable layer replaces the manual fixed-inset backdrop. */}
        <Popover open={showUserMenu} onOpenChange={setShowUserMenu}>
          <PopoverTrigger asChild>
            <button
              className="flex items-center gap-2 rounded-lg p-2 hover:bg-gray-100"
              aria-label="User menu"
              // Two accounts can share one full name: the signed-in email (also in the menu) tells them apart.
              title={user?.email ? `${displayName} (${user.email})` : displayName}
            >
              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-primary-600">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  (user?.fullName || user?.name)?.[0]?.toUpperCase() || <User className="h-4 w-4" />
                )}
              </div>
              {/* The PERSON on top, their role underneath. It used to lead
                  with the tenant name and fold "name · role" into the small
                  line below, which read as the tenant being the identity —
                  and for an SSO user whose display name falls back to their
                  email, the top line was the only thing that looked like a
                  name. The tenant/Platform context stays on the second line,
                  where it still answers "which context am I acting in". */}
              <div className="hidden text-left md:block">
                <p className="max-w-[12rem] truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {displayName}
                </p>
                <p className="max-w-[12rem] truncate text-xs text-gray-500">
                  {activeContext} · {roleLabel}
                </p>
              </div>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </button>
          </PopoverTrigger>
          {/* Beskar dropdown (kit §06): 13px radius, 5px padding, 9px-radius
              rows, hairline group separators. Identity block → Theme group →
              navigation → destructive, each visually grouped and aligned on a
              single 12px text inset. Same items, same handlers — only the
              mount/animation mechanics changed. */}
          <PopoverContent align="end" sideOffset={8} className="w-64 rounded-xl border bg-white p-1.5 shadow-lg">
                <div className="px-3 py-2.5">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {displayName}
                  </p>
                  <p className="truncate text-xs text-gray-500">{user?.email}</p>
                  <p className="mt-1.5 inline-flex max-w-full items-center truncate rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                    {activeContext} · {roleLabel}
                  </p>
                </div>

                <div className="my-1.5 h-px bg-gray-100 dark:bg-gray-800" />

                <div className="px-3 py-1.5">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Theme</p>
                  <ThemeToggle />
                </div>

                <div className="my-1.5 h-px bg-gray-100 dark:bg-gray-800" />

                <button
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                  onClick={() => {
                    setShowUserMenu(false)
                    navigate(hasRole('platform_admin') ? '/platform/settings' : '/tenant/settings')
                  }}
                >
                  <Settings className="h-4 w-4 shrink-0 text-gray-400" />
                  Settings
                </button>

                <button
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  Sign out
                </button>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  )
}
